import { useState } from 'react';
import { CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { pdfPageUrl } from '../../api/files';
import type { FilePage } from '../../api/types';

export interface PageStatusTileProps {
  page: FilePage;
  pdfGuid: string;
}

// Тайл одной страницы файла во время или после OCR-обработки. Показывает
// превью + статусный оверлей. Apple HIG: статус виден сразу, без надписей —
// иконка достаточна; цвет рамки усиливает значение.
export function PageStatusTile({ page, pdfGuid }: PageStatusTileProps) {
  const [loaded, setLoaded] = useState(false);
  const [failedPreview, setFailedPreview] = useState(false);

  const ringClass = {
    INIT: 'border-stroke',
    SELECT_PAGES: 'border-stroke',
    IN_PROGRESS: 'border-brand-primary shadow-soft',
    COMPLETED: 'border-green-300',
    FAILED: 'border-error/60',
  }[page.status];

  return (
    <div
      className={cn(
        'group relative flex aspect-[3/4] items-center justify-center overflow-hidden rounded-[14px] border-2 bg-white animate-enter',
        'transition-all duration-300 ease-[var(--ease-out-soft)]',
        ringClass,
      )}
      title={`Страница ${page.index} — ${labelFor(page.status)}`}
    >
      {!loaded && !failedPreview && (
        <div className="absolute inset-0 skeleton" />
      )}
      {!failedPreview && (
        <img
          src={pdfPageUrl(pdfGuid, page.index)}
          alt={`Страница ${page.index}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setFailedPreview(true)}
          className={cn(
            'h-full w-full object-contain transition-[opacity,filter] duration-500 ease-[var(--ease-out-soft)]',
            loaded ? 'opacity-100' : 'opacity-0',
            page.status === 'COMPLETED'
              ? 'grayscale-0'
              : page.status === 'IN_PROGRESS'
                ? 'opacity-90'
                : 'opacity-50 grayscale',
          )}
        />
      )}

      {/* Bottom gradient with page number */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-9 bg-gradient-to-t from-black/60 to-transparent" />
      <span className="absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
        Стр. {page.index}
      </span>

      {/* Status pill — top-right */}
      <StatusPill status={page.status} />

      {/* Soft sweep on IN_PROGRESS — like the Mac Activity Monitor stripes */}
      {page.status === 'IN_PROGRESS' && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-brand-primary/15 to-transparent"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s linear infinite',
          }}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: FilePage['status'] }) {
  const base =
    'absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-white/95 backdrop-blur-sm shadow-sm';

  switch (status) {
    case 'COMPLETED':
      return (
        <span className={base} aria-label="Готово">
          <CheckCircle2 size={16} className="text-[color:var(--success)]" />
        </span>
      );
    case 'IN_PROGRESS':
      return (
        <span className={base} aria-label="Обрабатывается">
          <Loader2 size={16} className="animate-spin text-brand-primary" />
        </span>
      );
    case 'FAILED':
      return (
        <span className={base} aria-label="Ошибка">
          <XCircle size={16} className="text-error" />
        </span>
      );
    default:
      return (
        <span className={base} aria-label="Ожидает">
          <Clock size={16} className="text-text-muted" />
        </span>
      );
  }
}

function labelFor(status: FilePage['status']): string {
  switch (status) {
    case 'COMPLETED':
      return 'готово';
    case 'IN_PROGRESS':
      return 'обрабатывается';
    case 'FAILED':
      return 'ошибка';
    default:
      return 'ожидает';
  }
}
